import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight, Plus, Lock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { financeAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { monthLabel } from '../../components/finance/financeUtils';
import { GuidanceBanner } from '../../components/finance/GuidanceBanner';
import { SummaryCards, EvolutionChart } from '../../components/finance/SummaryCards';
import { InvestmentPlan } from '../../components/finance/InvestmentPlan';
import { ChallengePlanner } from '../../components/finance/ChallengePlanner';
import { TransactionsTable } from '../../components/finance/TransactionsTable';
import { TransactionDialog } from '../../components/finance/TransactionDialog';
import { MobileBottomNav } from '../../components/mobile/MobileBottomNav';

const firstOfMonth = (d = new Date()) => new Date(d.getFullYear(), d.getMonth(), 1);

export default function MeuCaixa() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [view, setView] = useState(firstOfMonth());
  const [showAdd, setShowAdd] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  useEffect(() => {
    financeAPI.get().then((r) => setData(r.data)).catch(() => { toast.error('Não foi possível carregar o Meu Caixa'); setData({ transactions: [], challenge: {} }); });
  }, []);

  const save = async (payload) => {
    try {
      const r = await financeAPI.addTransaction(payload);
      setData((d) => ({ ...d, transactions: [r.data, ...d.transactions] }));
      setShowAdd(false);
      toast.success(payload.type === 'income' ? 'Entrada registrada' : 'Saída registrada');
    } catch (e) {
      toast.error(e.response?.data?.detail?.[0]?.msg || 'Erro ao salvar movimentação');
    }
  };

  const confirmDelete = async () => {
    const t = toDelete;
    setToDelete(null);
    try {
      await financeAPI.deleteTransaction(t.id);
      setData((d) => ({ ...d, transactions: d.transactions.filter((x) => x.id !== t.id) }));
      toast.success('Movimentação excluída');
    } catch { toast.error('Erro ao excluir'); }
  };

  if (!data) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-emerald-700" /></div>;
  const tx = data.transactions;

  return (
    <div className="min-h-screen bg-[#f5f7f4] pb-28" data-testid="meu-caixa-page">
      <header className="bg-white border-b border-slate-100 px-4 pt-5 pb-3 sticky top-0 z-30">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(user?.role?.toUpperCase() === 'EMPLOYEE' ? '/m/employee' : '/m')} className="p-1.5 -ml-1.5 rounded-lg hover:bg-slate-100" aria-label="Voltar" data-testid="meu-caixa-back"><ArrowLeft className="w-5 h-5" /></button>
          <div className="w-9 h-9 rounded-xl bg-emerald-700 text-white font-extrabold flex items-center justify-center">$</div>
          <div className="flex-1">
            <h1 className="font-bold text-slate-900 leading-tight">Meu Caixa</h1>
            <p className="text-[11px] text-slate-500">Finanças no seu ritmo</p>
          </div>
          <button onClick={() => setShowAdd(true)} aria-label="Nova movimentação" data-testid="finance-add-btn"
            className="flex items-center gap-1 rounded-xl bg-emerald-700 text-white text-sm font-semibold px-3 py-2 shadow-sm shadow-emerald-700/30 active:scale-95 transition-transform">
            <Plus className="w-4 h-4" /> Nova
          </button>
        </div>
        <p className="mt-2 text-[10px] text-slate-500 flex items-center gap-1" data-testid="meu-caixa-privacy"><Lock className="w-3 h-3" /> Seus dados são privados: só você vê. A empresa não tem acesso.</p>
      </header>

      <main className="p-4 space-y-4 max-w-xl mx-auto">
        <GuidanceBanner />
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button onClick={() => setView((v) => new Date(v.getFullYear(), v.getMonth() - 1, 1))} aria-label="Mês anterior" className="w-8 h-8 rounded-lg border border-slate-200 bg-white flex items-center justify-center" data-testid="finance-prev-month"><ChevronLeft className="w-4 h-4" /></button>
            <h2 className="text-sm font-bold min-w-32 text-center" data-testid="finance-month-title">{monthLabel(view)}</h2>
            <button onClick={() => setView((v) => new Date(v.getFullYear(), v.getMonth() + 1, 1))} aria-label="Próximo mês" className="w-8 h-8 rounded-lg border border-slate-200 bg-white flex items-center justify-center" data-testid="finance-next-month"><ChevronRight className="w-4 h-4" /></button>
          </div>
          <button onClick={() => setView(firstOfMonth())} className="text-xs border border-slate-200 bg-white rounded-lg px-3 py-1.5" data-testid="finance-today">Este mês</button>
        </div>
        <SummaryCards tx={tx} view={view} />
        <InvestmentPlan tx={tx} view={view} />
        <EvolutionChart tx={tx} view={view} />
        <TransactionsTable tx={tx} view={view} onDelete={setToDelete} />
        <ChallengePlanner initialPlan={data.challenge} />
      </main>

      <TransactionDialog open={showAdd} onClose={() => setShowAdd(false)} onSave={save} />
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="finance-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir esta movimentação?</AlertDialogTitle>
            <AlertDialogDescription>{toDelete?.description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="finance-delete-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700" data-testid="finance-delete-confirm">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <MobileBottomNav active="caixa" />
    </div>
  );
}
